import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Database, RefreshCw, ExternalLink, AlertCircle, CheckCircle2,
  Loader2, Edit3, X as XIcon, Save as SaveIcon, Filter, LineChart, Link2, Plus, Trash2, ArrowRightToLine, Users,
} from "lucide-react";
import { api, type PublishedArticleView, type PublishedKind, type ClusterView, type AlwaysLinkTarget, type ExploreAnchorItem, type MethodSupplyItem } from "../../lib/api.js";

/** GSC reports lag ~2 days. If our last sync is older than this, flag as stale. */
const GSC_STALE_DAYS = 2;

function formatCount(n: number | null): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function formatRelativeDays(iso: string | null): string {
  if (!iso) return "never";
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}

function isGscStale(iso: string | null): boolean {
  if (!iso) return false; // null = never synced, handled separately
  const days = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  return days > GSC_STALE_DAYS;
}

/**
 * Content DB page — the operator's view onto the local mirror of live Sanity
 * content. Powers the internal-link resolver.
 *
 * Surfaces:
 *   - 4 KPI tiles (total live + per-kind counts + last sync timestamp)
 *   - Filter chips (kind, cluster, search box)
 *   - Refresh Now button (calls POST /api/published-articles/sync)
 *   - Filterable table with inline URL-override editing
 */
export function ContentDbPage() {
  const qc = useQueryClient();
  const [kindFilter, setKindFilter] = useState<PublishedKind | null>(null);
  const [clusterFilter, setClusterFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showStale, setShowStale] = useState(false);

  const overviewQuery = useQuery({
    queryKey: ["published-articles", "overview"],
    queryFn: () => api.getPublishedOverview(),
    // Refetch when the user comes back to the tab
    refetchOnWindowFocus: true,
  });

  const articlesQuery = useQuery({
    queryKey: ["published-articles", { kindFilter, clusterFilter, search, showStale }],
    queryFn: () =>
      api.listPublishedArticles({
        kind: kindFilter ?? undefined,
        clusterId: clusterFilter ?? undefined,
        isLive: showStale ? undefined : true,
        q: search || undefined,
      }),
  });

  const clustersQuery = useQuery({
    queryKey: ["pipeline", "clusters"],
    queryFn: () => api.listClusters(),
  });

  const [syncProgress, setSyncProgress] = useState<string | null>(null);
  const syncMutation = useMutation({
    mutationFn: () =>
      api.syncPublishedArticles({}, (job) =>
        setSyncProgress(job.phase === "processing" && job.total ? `${job.current ?? 0}/${job.total}` : null),
      ),
    onSettled: () => setSyncProgress(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published-articles"] });
    },
  });

  const syncGscMutation = useMutation({
    mutationFn: () => api.syncGsc({}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published-articles"] });
    },
  });

  // Backfill cluster_id on live pages that never got one (mostly specific
  // treatment pages) so the internal-link resolver can surface them.
  const classifyMutation = useMutation({
    mutationFn: () => api.classifyUnclustered({}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published-articles"] });
    },
  });

  // Reconcile pipeline statuses against the Content DB — mark live articles
  // "published" so they can't be re-published by reflex from a stale "reviewing".
  const reconcileMutation = useMutation({
    mutationFn: () => api.reconcileStatus({}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-brand-700" strokeWidth={2.2} />
            <h1 className="text-2xl font-semibold">Content database</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Local mirror of live content from <code className="rounded bg-line px-1 py-0.5 font-mono text-xs">alternabe-cms</code> (Sanity). Used by the internal-link resolver
            to surface real, clickable URLs when generating briefs + articles.
            Auto-syncs nightly at 03:00; click Refresh now for an immediate sweep.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => syncGscMutation.mutate()}
            disabled={syncGscMutation.isPending}
            className="btn-ghost text-sm"
            title="Pull 28-day GSC performance (impressions, clicks, CTR, position, top queries) for every live URL"
          >
            {syncGscMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing GSC…
              </>
            ) : (
              <>
                <LineChart className="h-4 w-4" />
                Sync GSC now
              </>
            )}
          </button>
          <button
            onClick={() => {
              if (
                window.confirm(
                  "Classify unclustered pages into topical clusters so articles can internal-link to them.\n\n" +
                    "Runs the Haiku classifier on each unclustered page (~$0.01 each) and writes cluster assignments. " +
                    "Existing assignments are never touched; broad category hubs are left alone. Continue?",
                )
              ) {
                classifyMutation.mutate();
              }
            }}
            disabled={classifyMutation.isPending}
            className="btn-ghost text-sm"
            title="Fill cluster_id on live pages that never got one (mostly specific treatment pages) so the internal-link resolver can surface them as link targets."
          >
            {classifyMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Classifying…
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                Fix link clusters
              </>
            )}
          </button>
          <button
            onClick={() => reconcileMutation.mutate()}
            disabled={reconcileMutation.isPending}
            className="btn-ghost text-sm"
            title="Mark pipeline keywords 'published' when their article is already live here (e.g. published as a draft, then made live in Studio) — so they aren't re-published by reflex. Also runs automatically on every sync."
          >
            {reconcileMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Reconciling…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Reconcile status
              </>
            )}
          </button>
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="btn-primary text-sm"
          >
            {syncMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing{syncProgress ? ` ${syncProgress}` : "…"}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Refresh now
              </>
            )}
          </button>
        </div>
      </header>

      <AlwaysLinksManager />

      <ExploreAnchorsManager />
      <MethodSupplyPanel />

      {syncMutation.data && (
        <div className="rounded-lg border border-accent/30 bg-accent-soft px-4 py-2.5 text-xs">
          <div className="flex items-center gap-2 font-medium text-emerald-900">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {syncMutation.data.summary}
          </div>
          {syncMutation.data.warnings.length > 0 && (
            <ul className="mt-1.5 list-disc pl-5 text-warn">
              {syncMutation.data.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              {syncMutation.data.warnings.length > 5 && (
                <li className="text-muted">…and {syncMutation.data.warnings.length - 5} more.</li>
              )}
            </ul>
          )}
        </div>
      )}
      {syncMutation.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-xs text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Sync failed: {(syncMutation.error as Error).message}</span>
        </div>
      )}

      {classifyMutation.data && (
        <div className="rounded-lg border border-accent/30 bg-accent-soft px-4 py-2.5 text-xs">
          <div className="flex items-center gap-2 font-medium text-emerald-900">
            <Link2 className="h-4 w-4 shrink-0" />
            {classifyMutation.data.summary}
          </div>
          {classifyMutation.data.rows.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-muted">
              {classifyMutation.data.rows.slice(0, 12).map((r, i) => (
                <li key={i}>
                  <span className={r.outcome === "assigned" ? "font-medium text-emerald-700" : ""}>[{r.outcome}]</span>{" "}
                  {r.title} → {r.clusterName ?? "—"}
                  {r.confidence != null ? ` (${r.confidence})` : ""}
                </li>
              ))}
              {classifyMutation.data.rows.length > 12 && (
                <li className="text-muted">…and {classifyMutation.data.rows.length - 12} more.</li>
              )}
            </ul>
          )}
        </div>
      )}
      {classifyMutation.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-xs text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Classify failed: {(classifyMutation.error as Error).message}</span>
        </div>
      )}

      {reconcileMutation.data && (
        <div className="rounded-lg border border-accent/30 bg-accent-soft px-4 py-2.5 text-xs">
          <div className="flex items-center gap-2 font-medium text-emerald-900">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {reconcileMutation.data.summary}
          </div>
          {reconcileMutation.data.reconciled.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-muted">
              {reconcileMutation.data.reconciled.slice(0, 12).map((r, i) => (
                <li key={i}>
                  {r.keyword}{" "}
                  <span className="text-muted">
                    ({r.statusChanged ? `${r.from} → ${r.to}` : "publish date synced from Studio"})
                  </span>
                </li>
              ))}
              {reconcileMutation.data.reconciled.length > 12 && (
                <li className="text-muted">…and {reconcileMutation.data.reconciled.length - 12} more.</li>
              )}
            </ul>
          )}
        </div>
      )}
      {reconcileMutation.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-xs text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Reconcile failed: {(reconcileMutation.error as Error).message}</span>
        </div>
      )}

      {syncGscMutation.data?.ok && syncGscMutation.data.summary && (
        <div className="rounded-lg border border-accent/30 bg-accent-soft px-4 py-2.5 text-xs">
          <div className="flex items-center gap-2 font-medium text-emerald-900">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            GSC: {syncGscMutation.data.summary}
          </div>
          {syncGscMutation.data.warnings && syncGscMutation.data.warnings.length > 0 && (
            <ul className="mt-1.5 list-disc pl-5 text-warn">
              {syncGscMutation.data.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              {syncGscMutation.data.warnings.length > 5 && (
                <li className="text-muted">…and {syncGscMutation.data.warnings.length - 5} more.</li>
              )}
            </ul>
          )}
        </div>
      )}
      {syncGscMutation.data?.ok === false && (
        <div className="flex items-start gap-2 rounded-lg border border-warn/30 bg-warn/5 px-4 py-2.5 text-xs text-warn">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>GSC sync skipped: {syncGscMutation.data.error}</span>
        </div>
      )}
      {syncGscMutation.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-xs text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>GSC sync failed: {(syncGscMutation.error as Error).message}</span>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile label="Live total" value={overviewQuery.data?.counts.total} />
        <KpiTile label="Blog articles" value={overviewQuery.data?.counts.article} />
        <KpiTile label="Treatment hubs" value={overviewQuery.data?.counts.treatment_category} />
        <KpiTile label="Treatments" value={overviewQuery.data?.counts.treatment_subcategory} />
      </div>
      {overviewQuery.data?.lastSyncedAt && (
        <div className="text-xs text-muted">
          Last synced: <span className="font-mono">{new Date(overviewQuery.data.lastSyncedAt).toLocaleString("en-GB")}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 text-xs text-muted">
          <Filter className="h-3.5 w-3.5" />
          Filter:
        </div>
        <div className="flex flex-wrap gap-1">
          <FilterChip active={kindFilter === null} onClick={() => setKindFilter(null)}>All</FilterChip>
          <FilterChip active={kindFilter === "article"} onClick={() => setKindFilter("article")}>Blog</FilterChip>
          <FilterChip active={kindFilter === "treatment_category"} onClick={() => setKindFilter("treatment_category")}>Hubs</FilterChip>
          <FilterChip active={kindFilter === "treatment_subcategory"} onClick={() => setKindFilter("treatment_subcategory")}>Treatments</FilterChip>
        </div>
        <select
          value={clusterFilter ?? ""}
          onChange={(e) => setClusterFilter(e.target.value || null)}
          className="input w-48 text-xs"
        >
          <option value="">All clusters</option>
          {(clustersQuery.data?.clusters ?? []).map((c: ClusterView) => (
            <option key={c.id} value={c.id}>{c.nameEn}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search title / slug / topic…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-xs"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input
            type="checkbox"
            checked={showStale}
            onChange={(e) => setShowStale(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Show stale (gone from Sanity)
        </label>
      </div>

      {/* Table */}
      <ArticleTable
        articles={articlesQuery.data?.articles ?? []}
        loading={articlesQuery.isPending}
        onChanged={() => qc.invalidateQueries({ queryKey: ["published-articles"] })}
        clusters={clustersQuery.data?.clusters ?? []}
      />
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-brand">{value ?? "—"}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "rounded-full bg-brand px-3 py-1 text-xs font-medium text-white"
          : "rounded-full bg-line px-3 py-1 text-xs font-medium text-muted hover:bg-line/80 hover:text-ink"
      }
    >
      {children}
    </button>
  );
}

function ArticleTable({
  articles,
  loading,
  onChanged,
  clusters,
}: {
  articles: PublishedArticleView[];
  loading: boolean;
  onChanged: () => void;
  clusters: ClusterView[];
}) {
  const clusterById = new Map(clusters.map((c) => [c.id, c]));
  if (loading) {
    return (
      <div className="card p-6 text-sm text-muted">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading mirrored content…
      </div>
    );
  }
  if (articles.length === 0) {
    return (
      <div className="card p-6 text-center text-sm text-muted">
        No content mirrored yet. Click <strong>Refresh now</strong> above to pull from Sanity.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-white">
      <table className="min-w-full divide-y divide-line text-sm">
        <thead className="bg-canvas text-2xs uppercase tracking-wider text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Title</th>
            <th className="px-3 py-2 text-left">Kind</th>
            <th className="px-3 py-2 text-left">Cluster</th>
            <th className="px-3 py-2 text-left">Topics</th>
            <th className="px-3 py-2 text-left" title="Operator-set keywords — used as internal-link anchors + link matching">Keywords</th>
            <th className="px-3 py-2 text-left">URL</th>
            <th className="px-3 py-2 text-left" title="Google Search Console — 28-day impressions / clicks · CTR / avg position">GSC (28d)</th>
            <th className="px-3 py-2 text-left">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {articles.map((a) => (
            <Row key={a.sanityId} article={a} cluster={a.clusterId ? clusterById.get(a.clusterId) : undefined} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  article,
  cluster,
  onChanged,
}: {
  article: PublishedArticleView;
  cluster: ClusterView | undefined;
  onChanged: () => void;
}) {
  const effectiveUrl = article.urlOverride || article.url;
  const kindLabel =
    article.kind === "article" ? "Blog" :
    article.kind === "treatment_category" ? "Hub" :
    "Treatment";
  const kindColor =
    article.kind === "article" ? "bg-sky-100 text-sky-700" :
    article.kind === "treatment_category" ? "bg-violet-100 text-violet-700" :
    "bg-emerald-100 text-emerald-700";

  return (
    <tr className={article.isLive ? "" : "bg-canvas text-muted"}>
      <td className="max-w-xs px-3 py-2.5">
        <div className="truncate font-medium text-ink" dir="auto" title={article.title}>{article.title}</div>
        {article.excerpt && (
          <div className="truncate text-xs text-muted" dir="auto" title={article.excerpt}>
            {article.excerpt.slice(0, 80)}{article.excerpt.length > 80 ? "…" : ""}
          </div>
        )}
        {!article.isLive && (
          <span className="mt-0.5 inline-block rounded bg-warn/10 px-1.5 py-0.5 text-[10px] text-warn">stale (not in Sanity)</span>
        )}
        {article.kind === "article" && <MoveToPipelineButton article={article} />}
      </td>
      <td className="px-3 py-2.5 align-top">
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${kindColor}`}>{kindLabel}</span>
      </td>
      <td className="px-3 py-2.5 align-top">
        {cluster ? (
          <span className="text-xs">
            {cluster.nameEn}
            {article.clusterConfidence !== null && (
              <span className="ml-1 text-muted">({Math.round(article.clusterConfidence * 100)}%)</span>
            )}
          </span>
        ) : (
          <span className="text-xs text-muted italic">unclassified</span>
        )}
      </td>
      <td className="max-w-xs px-3 py-2.5 align-top">
        <div className="flex flex-wrap gap-1">
          {[...article.topicTags, ...article.derivedTopics].slice(0, 5).map((t, i) => (
            <span key={`${t}-${i}`} className="rounded-full bg-line-soft px-1.5 py-0.5 text-[10px] text-muted-dark" dir="auto">
              {t}
            </span>
          ))}
        </div>
      </td>
      <td className="max-w-xs px-3 py-2.5 align-top">
        <KeywordsCell article={article} onChanged={onChanged} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <UrlCell article={article} effectiveUrl={effectiveUrl} onChanged={onChanged} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <GscCell article={article} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <div className="text-xs text-muted">
          {new Date(article.sanityUpdatedAt).toLocaleDateString("en-GB")}
        </div>
      </td>
    </tr>
  );
}

function MoveToPipelineButton({ article }: { article: PublishedArticleView }) {
  const move = useMutation({
    mutationFn: () => api.moveToPipeline(article.sanityId),
    onSuccess: (r) => {
      if (!r.ok || !r.keywordId) return;
      // Jump to the Pipeline and open the adopted keyword (deep-link via hash param).
      window.location.hash = `#/pipeline?open=${encodeURIComponent(r.keywordId)}`;
    },
  });
  const err = move.isError ? (move.error as Error).message : (move.data && !move.data.ok ? move.data.error : null);
  return (
    <div className="mt-1">
      <button
        className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[10px] font-medium text-brand hover:bg-brand/5 disabled:opacity-50"
        disabled={move.isPending}
        onClick={() => move.mutate()}
        title="Adopt this live article into the pipeline — keeps its original publish date, so a later re-publish won't reset it. Then Brief → Produce → Publish (or Regenerate) to update it.">
        {move.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightToLine className="h-3 w-3" />} Move to pipeline
      </button>
      {err && <div className="mt-0.5 text-[10px] text-danger" dir="auto">{err}</div>}
    </div>
  );
}

function GscCell({ article }: { article: PublishedArticleView }) {
  const {
    gscImpressions28d: imp,
    gscClicks28d: clk,
    gscCtr28d: ctr,
    gscAvgPosition28d: pos,
    gscTopQueries: queries,
    gscLastSyncedAt: syncedAt,
  } = article;

  // Never synced — show a quiet placeholder so operators know GSC sync hasn't run yet
  // (or that GSC integration isn't configured).
  if (!syncedAt) {
    return <span className="text-xs italic text-muted">never synced</span>;
  }

  // Synced but the URL has no impressions in the 28-day window — common for
  // brand-new articles (GSC takes ~2-3 days to index).
  const noData = imp === null || imp === 0;
  if (noData) {
    return (
      <div className="text-xs">
        <div className="italic text-muted">no data</div>
        <div className="text-[10px] text-muted">synced {formatRelativeDays(syncedAt)}</div>
      </div>
    );
  }

  const stale = isGscStale(syncedAt);
  const ctrPct = ctr !== null ? `${(ctr * 100).toFixed(1)}%` : "—";
  const posLabel = pos !== null ? pos.toFixed(1) : "—";

  return (
    <div className="text-xs">
      <div className="font-mono font-medium tabular-nums text-ink">{formatCount(imp)} imp</div>
      <div className="text-muted">
        <span className="font-mono tabular-nums">{formatCount(clk)}</span> clk · <span className="font-mono tabular-nums">{ctrPct}</span>
      </div>
      <div className="text-muted">
        pos <span className="font-mono font-medium tabular-nums text-ink">{posLabel}</span>
        {queries && queries.length > 0 && (
          <>
            {" · "}
            <QueriesPopover queries={queries} />
          </>
        )}
      </div>
      <div className={stale ? "text-[10px] text-warn" : "text-[10px] text-muted"}>
        {stale && <AlertCircle className="mr-0.5 inline h-2.5 w-2.5" />}
        synced {formatRelativeDays(syncedAt)}{stale ? " (stale)" : ""}
      </div>
    </div>
  );
}

function QueriesPopover({ queries }: { queries: NonNullable<PublishedArticleView["gscTopQueries"]> }) {
  return (
    <span className="group relative cursor-help text-brand-700 underline decoration-dotted">
      {queries.length} {queries.length === 1 ? "query" : "queries"}
      <div className="invisible absolute bottom-full left-0 z-50 mb-1 w-80 -translate-x-1/2 rounded-lg border border-line bg-white p-3 shadow-lg group-hover:visible">
        <div className="mb-1.5 text-2xs uppercase tracking-wider text-muted">Top queries (28d)</div>
        <table className="w-full text-xs">
          <thead className="text-2xs uppercase tracking-wider text-muted">
            <tr>
              <th className="pb-1 text-left font-normal">Query</th>
              <th className="pb-1 text-right font-normal">Imp</th>
              <th className="pb-1 text-right font-normal">Clk</th>
              <th className="pb-1 text-right font-normal">Pos</th>
            </tr>
          </thead>
          <tbody>
            {queries.slice(0, 10).map((q, i) => (
              <tr key={`${q.query}-${i}`} className="border-t border-line/50">
                <td className="max-w-[180px] truncate py-0.5 pr-2" dir="auto" title={q.query}>{q.query}</td>
                <td className="py-0.5 text-right font-mono tabular-nums">{formatCount(q.impressions)}</td>
                <td className="py-0.5 text-right font-mono tabular-nums">{formatCount(q.clicks)}</td>
                <td className="py-0.5 text-right font-mono tabular-nums">{q.position.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </span>
  );
}

function KeywordsCell({ article, onChanged }: { article: PublishedArticleView; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [main, setMain] = useState(article.mainKeyword ?? "");
  const [secondary, setSecondary] = useState<string[]>(article.secondaryKeywords ?? []);
  const [newKw, setNewKw] = useState("");
  const [suggestions, setSuggestions] = useState<{ main: string[]; secondary: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openEdit = async () => {
    setMain(article.mainKeyword ?? "");
    setSecondary(article.secondaryKeywords ?? []);
    setError(null);
    setEditing(true);
    setSuggestions(null);
    try { setSuggestions(await api.getKeywordSuggestions(article.sanityId)); } catch { /* suggestions are best-effort */ }
  };

  const addSecondary = (kw: string) => {
    const v = kw.trim();
    if (!v) return;
    setSecondary((prev) => (prev.some((s) => s.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]));
    setNewKw("");
  };
  const removeSecondary = (kw: string) => setSecondary((prev) => prev.filter((s) => s !== kw));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patchPublishedArticle(article.sanityId, { mainKeyword: main.trim() || null, secondaryKeywords: secondary });
      setEditing(false);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  if (editing) {
    const mainLc = main.trim().toLowerCase();
    const suggMain = (suggestions?.main ?? []).filter((s) => s.toLowerCase() !== mainLc).slice(0, 3);
    const suggSecondary = (suggestions?.secondary ?? [])
      .filter((s) => s.toLowerCase() !== mainLc && !secondary.some((x) => x.toLowerCase() === s.toLowerCase()))
      .slice(0, 8);
    return (
      <div className="flex min-w-[230px] flex-col gap-1.5">
        <input
          value={main}
          onChange={(e) => setMain(e.target.value)}
          placeholder="Main keyword (link anchor)"
          className="input text-xs"
          dir="auto"
          autoFocus
        />
        {suggMain.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {suggMain.map((s, i) => (
              <button key={i} onClick={() => setMain(s)} className="rounded-full border border-dashed border-line px-1.5 py-0.5 text-[10px] text-muted hover:bg-line" dir="auto" title="Use as main keyword">+ {s}</button>
            ))}
          </div>
        )}
        {secondary.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {secondary.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-line-soft px-1.5 py-0.5 text-[10px] text-muted-dark" dir="auto">
                {s}
                <button onClick={() => removeSecondary(s)} className="text-muted hover:text-danger" title="Remove"><XIcon className="h-2.5 w-2.5" /></button>
              </span>
            ))}
          </div>
        )}
        <input
          value={newKw}
          onChange={(e) => setNewKw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSecondary(newKw); } }}
          placeholder="Add secondary keyword + Enter"
          className="input text-xs"
          dir="auto"
        />
        {suggSecondary.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {suggSecondary.map((s, i) => (
              <button key={i} onClick={() => addSecondary(s)} className="rounded-full border border-dashed border-line px-1.5 py-0.5 text-[10px] text-muted hover:bg-line" dir="auto" title="Add as secondary keyword">+ {s}</button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1">
          <button onClick={save} disabled={busy} className="btn btn-primary text-xs">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <SaveIcon className="h-3 w-3" />}
            Save
          </button>
          <button onClick={() => setEditing(false)} className="btn btn-ghost text-xs"><XIcon className="h-3 w-3" />Cancel</button>
        </div>
        {error && <div className="text-2xs text-danger">{error}</div>}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1.5">
      <div className="flex min-w-0 flex-col gap-1">
        {article.mainKeyword ? (
          <span className="inline-block max-w-[160px] truncate rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-medium text-brand-700" dir="auto" title={article.mainKeyword}>{article.mainKeyword}</span>
        ) : (
          <span className="text-[10px] italic text-muted">no main KW</span>
        )}
        {article.secondaryKeywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {article.secondaryKeywords.slice(0, 4).map((s, i) => (
              <span key={i} className="rounded-full bg-line-soft px-1.5 py-0.5 text-[10px] text-muted-dark" dir="auto">{s}</span>
            ))}
            {article.secondaryKeywords.length > 4 && <span className="text-[10px] text-muted">+{article.secondaryKeywords.length - 4}</span>}
          </div>
        )}
      </div>
      <button onClick={openEdit} className="rounded p-1 text-muted hover:bg-line hover:text-ink" title="Edit keywords"><Edit3 className="h-3 w-3" /></button>
    </div>
  );
}

function UrlCell({ article, effectiveUrl, onChanged }: { article: PublishedArticleView; effectiveUrl: string; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(article.urlOverride ?? "");
  const [note, setNote] = useState(article.urlOverrideNote ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patchPublishedArticle(article.sanityId, {
        urlOverride: draft.trim() || null,
        urlOverrideNote: note.trim() || null,
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  const clearOverride = async () => {
    setBusy(true);
    try {
      await api.patchPublishedArticle(article.sanityId, { urlOverride: null, urlOverrideNote: null });
      setDraft("");
      setNote("");
      setEditing(false);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5">
        <input
          type="url"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={article.url}
          className="input font-mono text-xs"
          autoFocus
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (why was this overridden?)"
          className="input text-xs"
        />
        <div className="flex items-center gap-1">
          <button onClick={save} disabled={busy} className="btn btn-primary text-xs">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <SaveIcon className="h-3 w-3" />}
            Save
          </button>
          <button onClick={() => { setEditing(false); setDraft(article.urlOverride ?? ""); setNote(article.urlOverrideNote ?? ""); }} className="btn btn-ghost text-xs">
            <XIcon className="h-3 w-3" />
            Cancel
          </button>
          {article.urlOverride && (
            <button onClick={clearOverride} disabled={busy} className="btn btn-ghost text-xs" title="Clear override (revert to computed URL)">
              <RefreshCw className="h-3 w-3" />
              Reset
            </button>
          )}
        </div>
        {error && <div className="text-2xs text-danger">{error}</div>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <a
        href={effectiveUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-0.5 truncate font-mono text-xs text-brand-700 hover:underline"
        title={effectiveUrl}
      >
        {effectiveUrl.replace(/^https?:\/\/(www\.)?/, "")}
        <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
      </a>
      {article.urlOverride && (
        <span className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800" title={article.urlOverrideNote ?? "Override active"}>
          override
        </span>
      )}
      <button
        onClick={() => setEditing(true)}
        className="rounded p-1 text-muted hover:bg-line hover:text-ink"
        title="Edit URL override"
      >
        <Edit3 className="h-3 w-3" />
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Always-link targets — site-wide pinned links woven into every article.
// ───────────────────────────────────────────────────────────────────────────
function AlwaysLinksManager() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["always-links"], queryFn: () => api.listAlwaysLinks() });
  const targets = data?.targets ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["always-links"] });

  return (
    <div className="rounded-lg border border-line bg-white">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-brand-700" />
          <span className="text-sm font-semibold">Always-link targets</span>
          <span className="text-xs text-muted">{targets.length} configured · woven into every article</span>
        </div>
        <span className="text-muted">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-line p-4">
          <p className="text-xs text-muted">
            Site-wide links (homepage, explore, treatment categories…) that the resolver pins into every produced article, on top of contextual links. Give each a main anchor; add secondary anchors so the wording varies across articles.
          </p>
          <div className="space-y-2">
            {targets.map((t) => <AlwaysLinkRow key={t.id} target={t} onChanged={invalidate} />)}
          </div>
          <AlwaysLinkRow onChanged={invalidate} />
        </div>
      )}
    </div>
  );
}

function AlwaysLinkRow({ target, onChanged }: { target?: AlwaysLinkTarget; onChanged: () => void }) {
  const isNew = !target;
  const [url, setUrl] = useState(target?.url ?? "");
  const [mainAnchor, setMainAnchor] = useState(target?.mainAnchor ?? "");
  const [secondary, setSecondary] = useState((target?.secondaryAnchors ?? []).join(", "));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = isNew
    ? url.trim() !== "" || mainAnchor.trim() !== ""
    : url !== target!.url || mainAnchor !== target!.mainAnchor || secondary !== (target!.secondaryAnchors ?? []).join(", ");

  const save = async () => {
    if (!url.trim() || !mainAnchor.trim()) { setError("URL and main anchor are required"); return; }
    setBusy(true); setError(null);
    try {
      await api.upsertAlwaysLink({
        id: target?.id,
        url: url.trim(),
        mainAnchor: mainAnchor.trim(),
        secondaryAnchors: secondary.split(",").map((s) => s.trim()).filter(Boolean),
        active: target?.active ?? true,
      });
      if (isNew) { setUrl(""); setMainAnchor(""); setSecondary(""); }
      onChanged();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const toggleActive = async () => {
    if (!target) return;
    setBusy(true);
    try { await api.upsertAlwaysLink({ id: target.id, url: target.url, mainAnchor: target.mainAnchor, secondaryAnchors: target.secondaryAnchors, active: !target.active }); onChanged(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!target) return;
    setBusy(true);
    try { await api.deleteAlwaysLink(target.id); onChanged(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-md border p-2 ${isNew ? "border-dashed border-line" : target!.active ? "border-line" : "border-line bg-canvas opacity-60"}`}>
      <div className="grid grid-cols-12 items-center gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.alternabe.co.il/explore" className="input col-span-4 font-mono text-xs" dir="ltr" />
        <input value={mainAnchor} onChange={(e) => setMainAnchor(e.target.value)} placeholder="main anchor (e.g. AlternaBe)" className="input col-span-3 text-xs" dir="auto" />
        <input value={secondary} onChange={(e) => setSecondary(e.target.value)} placeholder="secondary anchors, comma-separated" className="input col-span-3 text-xs" dir="auto" />
        <div className="col-span-2 flex items-center justify-end gap-1">
          {dirty && (
            <button onClick={save} disabled={busy} className="btn btn-primary text-2xs" title="Save">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : isNew ? <Plus className="h-3 w-3" /> : <SaveIcon className="h-3 w-3" />}
              {isNew ? "Add" : "Save"}
            </button>
          )}
          {!isNew && (
            <>
              <button onClick={toggleActive} disabled={busy} className="rounded p-1 text-2xs text-muted hover:bg-line" title={target!.active ? "Disable" : "Enable"}>
                {target!.active ? "on" : "off"}
              </button>
              <button onClick={remove} disabled={busy} className="rounded p-1 text-muted hover:bg-line hover:text-danger" title="Delete">
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      </div>
      {error && <div className="mt-1 text-2xs text-danger">{error}</div>}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Therapist supply per method — the roster feed behind `clusters.supply_gate`.
// The gate has existed since migration 033 and meant nothing until this landed,
// because there was no roster to compare it against. Read-only: the site owns
// therapist data, and recording supply never moves a cluster's phase.
// ───────────────────────────────────────────────────────────────────────────
function MethodSupplyPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [onlyWaking, setOnlyWaking] = useState(false);
  const { data } = useQuery({ queryKey: ["method-supply"], queryFn: () => api.listMethodSupply() });
  const sync = useMutation({
    mutationFn: () => api.syncMethodSupply(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["method-supply"] }),
  });

  const items = data?.items ?? [];
  const shown = onlyWaking ? items.filter((i) => i.wouldWake) : items;
  const g = data?.gates;
  const synced = data?.lastSyncedAt ? new Date(data.lastSyncedAt).toLocaleString() : "never";

  return (
    <div className="rounded-lg border border-line bg-white">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-brand-700" />
          <span className="text-sm font-semibold">Therapist supply per method</span>
          <span className="text-xs text-muted">
            {items.length} methods{g ? ` · ${g.wouldWake} would wake · ${g.blocked} blocked` : ""} · synced {synced}
          </span>
        </div>
        <span className="text-muted">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-line p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="max-w-3xl text-xs text-muted">
              Counted from the site&apos;s own public roster. A therapist practising several methods counts under each one, because the question is
              &ldquo;can we serve this query&rdquo;, not how many distinct people we have. <span className="font-medium">Nothing here changes a cluster</span> —
              a method that passes its gate is listed as <span className="font-medium">would wake</span> and stays at phase 3 until you move it.
            </p>
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-secondary shrink-0 text-2xs" title="Re-read the live roster now. Read-only against the site.">
              {sync.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Sync from site
            </button>
          </div>

          {sync.data && <div className="rounded bg-canvas px-2 py-1 text-2xs text-muted">{sync.data.summary}</div>}
          {sync.isError && (
            <div className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-2xs text-rose-900">
              Sync failed: {(sync.error as Error).message}. Previous counts are unchanged.
            </div>
          )}
          {data?.lastRun && data.lastRun.ok === 0 && (
            <div className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-2xs text-rose-900">
              Last sync FAILED ({new Date(data.lastRun.ran_at).toLocaleString()}): {data.lastRun.error}. The counts below are from the last good run.
            </div>
          )}
          {g && (
            <div className="flex flex-wrap items-center gap-2 text-2xs">
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800">{g.wouldWake} would wake</span>
              <span className="rounded-full bg-canvas px-2 py-0.5 text-muted">{g.alreadyLive} already live</span>
              <span className="rounded-full bg-canvas px-2 py-0.5 text-muted">{g.blocked} blocked</span>
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-900" title="loc-* clusters gate on CITY counts, which a per-method lookup cannot answer.">
                {g.cityClusters} city clusters need city counts
              </span>
              <button onClick={() => setOnlyWaking((v) => !v)} className="ml-auto rounded border border-line px-2 py-0.5 hover:bg-canvas">
                {onlyWaking ? "Show all" : "Only would-wake"}
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-2xs uppercase tracking-wide text-muted">
                  <th className="px-2 py-1 text-left">Method</th>
                  <th className="px-2 py-1 text-right" title="Therapists listing this method">Therapists</th>
                  <th className="px-2 py-1 text-right" title="How many carry a profile photo — what a 'meet the therapists' card needs">With photo</th>
                  <th className="px-2 py-1 text-right">Gate</th>
                  <th className="px-2 py-1 text-left">Status</th>
                  <th className="px-2 py-1 text-left">Top cities</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {shown.map((m) => <MethodSupplyRow key={m.methodSlug} m={m} />)}
              </tbody>
            </table>
          </div>

          {data && data.unmatched.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-2xs text-amber-900">
              <span className="font-medium">{data.unmatched.length} gated cluster(s) the roster does not mention at all.</span>{" "}
              Usually a slug that drifted from the site&apos;s: {data.unmatched.slice(0, 8).map((u) => u.clusterId).join(", ")}
              {data.unmatched.length > 8 ? "…" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MethodSupplyRow({ m }: { m: MethodSupplyItem }) {
  const status = m.clusterId === null
    ? { label: "no cluster", cls: "bg-canvas text-muted", title: "The roster reports this method but no gated cluster owns it." }
    : m.wouldWake
      ? { label: "would wake", cls: "bg-emerald-50 text-emerald-800", title: "Passes its gate but is still parked at phase 3." }
      : m.passes
        ? { label: "live", cls: "bg-canvas text-muted", title: "Passes its gate and is already in production." }
        : { label: "blocked", cls: "bg-rose-50 text-rose-900", title: "Below its supply gate — commercial content stays gated." };

  return (
    <tr>
      <td className="px-2 py-1">
        <span dir="rtl" className="font-medium">{m.nameHe ?? m.methodSlug}</span>
        <span className="ml-2 font-mono text-2xs text-muted">/explore/{m.methodSlug}</span>
      </td>
      <td className="px-2 py-1 text-right tabular-nums">{m.therapistCount}</td>
      <td className="px-2 py-1 text-right tabular-nums text-muted">{m.withPhoto}</td>
      <td className="px-2 py-1 text-right tabular-nums text-muted">{m.supplyGate ?? "—"}</td>
      <td className="px-2 py-1">
        <span className={`rounded-full px-2 py-0.5 text-2xs ${status.cls}`} title={status.title}>{status.label}</span>
      </td>
      <td className="px-2 py-1 text-2xs text-muted" dir="rtl">
        {m.topCities.length === 0 ? "—" : m.topCities.map((c) => `${c.cityHe ?? c.citySlug} (${c.count})`).join(" · ")}
      </td>
    </tr>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Provider anchors (/explore) — the therapist-directory CTA per treatment.
// The provider noun varies by discipline (מורי יוגה vs מטפלים בדיקור סיני), so
// the operator curates one per treatment; the resolver reads it when it adds
// the find-a-therapist link for the article's own treatment.
// ───────────────────────────────────────────────────────────────────────────
function ExploreAnchorsManager() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["explore-anchors"], queryFn: () => api.listExploreAnchors() });
  const items = data?.items ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["explore-anchors"] });
  const sync = useMutation({ mutationFn: () => api.syncExploreAnchors(), onSuccess: invalidate });
  const operatorCount = items.filter((i) => i.source === "operator").length;

  return (
    <div className="rounded-lg border border-line bg-white">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-brand-700" />
          <span className="text-sm font-semibold">Provider anchors (/explore)</span>
          <span className="text-xs text-muted">{items.length} treatments · {operatorCount} curated · find-a-therapist CTA per treatment</span>
        </div>
        <span className="text-muted">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-line p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="max-w-3xl text-xs text-muted">
              The anchor the resolver uses when it links a treatment article to its <span className="font-mono">/explore</span> therapist directory. The provider noun differs by discipline, so <span dir="rtl">"מטפלים ב…"</span> is only right for hands-on therapies. Edit the ones that should read <span dir="rtl">"מורי יוגה"</span>, <span dir="rtl">"מדריכי צ׳י קונג"</span>, <span dir="rtl">"מאמנים אישיים"</span>, etc. Your edits are kept; the nightly sync only fills in new hubs.
            </p>
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-secondary shrink-0 text-2xs" title="Fetch any new /explore hubs and refresh treatment names from the site. Never overwrites your edits.">
              {sync.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Sync from site
            </button>
          </div>
          {sync.data && <div className="rounded bg-canvas px-2 py-1 text-2xs text-muted">{sync.data.summary}</div>}
          {data && data.unseeded > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-2xs text-amber-900">
              {data.unseeded} hub(s) not yet seeded — click "Sync from site" to pull their Hebrew treatment names.
            </div>
          )}
          <div className="grid grid-cols-12 gap-2 px-1 text-2xs font-semibold uppercase tracking-wide text-muted">
            <span className="col-span-3">Treatment</span>
            <span className="col-span-6">Provider anchor</span>
            <span className="col-span-3 text-right">Source</span>
          </div>
          <div className="space-y-1">
            {items.map((it) => <ExploreAnchorRow key={it.methodSlug} item={it} onChanged={invalidate} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ExploreAnchorRow({ item, onChanged }: { item: ExploreAnchorItem; onChanged: () => void }) {
  const [anchor, setAnchor] = useState(item.anchor);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = anchor.trim() !== item.anchor;
  const isOperator = item.source === "operator";

  // Keep the field in sync when the query refetches (e.g. after a sync).
  const [lastAnchor, setLastAnchor] = useState(item.anchor);
  if (item.anchor !== lastAnchor && !dirty) { setLastAnchor(item.anchor); setAnchor(item.anchor); }

  const save = async () => {
    if (!anchor.trim()) { setError("Anchor cannot be empty"); return; }
    setBusy(true); setError(null);
    try { await api.upsertExploreAnchor({ methodSlug: item.methodSlug, anchor: anchor.trim() }); onChanged(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.deleteExploreAnchor(item.methodSlug);
      // Adopt the recomputed default locally so the field snaps back (clears dirty).
      setAnchor(r.anchor.anchor); setLastAnchor(r.anchor.anchor);
      onChanged();
    }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-line p-2">
      <div className="grid grid-cols-12 items-center gap-2">
        <div className="col-span-3 min-w-0">
          <div className="truncate text-xs font-medium" dir="auto" title={item.treatmentName}>{item.treatmentName}</div>
          <a href={item.url} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 truncate font-mono text-2xs text-brand-700 hover:underline" dir="ltr">
            /explore/{item.methodSlug} <ExternalLink className="h-2.5 w-2.5 shrink-0" />
          </a>
        </div>
        <input value={anchor} onChange={(e) => setAnchor(e.target.value)} className="input col-span-6 text-xs" dir="rtl" placeholder="מטפלים ב…" />
        <div className="col-span-3 flex items-center justify-end gap-1">
          {dirty ? (
            <button onClick={save} disabled={busy} className="btn btn-primary text-2xs" title="Save (marks this treatment curated)">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <SaveIcon className="h-3 w-3" />} Save
            </button>
          ) : (
            <span className={`rounded px-1.5 py-0.5 text-2xs ${isOperator ? "bg-emerald-50 text-emerald-800" : "bg-canvas text-muted"}`} title={isOperator ? "Operator-curated" : "Auto default / suggestion — review it"}>
              {isOperator ? "curated" : "suggested"}
            </span>
          )}
          {isOperator && !dirty && (
            <button onClick={reset} disabled={busy} className="rounded p-1 text-muted hover:bg-line hover:text-danger" title="Reset to the default suggestion">
              <XIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {error && <div className="mt-1 text-2xs text-danger">{error}</div>}
    </div>
  );
}
